import React, { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import logo from '../../assets/logo1.png';
import { accessRequestAPI } from '../../services/api';
import './AccessRequestPopup.css';

// Fixed production URL — this is printed on physical flyers as a QR code,
// so it must never change. Scanning it opens this same popup on the visitor's phone.
const ACCESS_QR_URL = 'https://ebuddhi.com/';

const DESIGNATION_OPTIONS = [
  'Student',
  'Instructor / Educator',
  'Working Professional',
  'Institution / Organization',
  'Other',
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const INVALID_EMAIL = 'Please enter a valid email address (e.g. name@example.com).';
const ALREADY_REGISTERED = 'This email has already been registered.';

const AccessRequestPopup = ({ onClose }) => {
  const [form, setForm] = useState({ fullName: '', phoneNumber: '', email: '', designation: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Esc closes the popup; the page behind it doesn't scroll while it's open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const prevOverflow = document.body.style.overflow;
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [onClose]);

  // Last duplicate-check result, so submit can reuse (or await) it instead of
  // the blur-triggered check disabling the button mid-click.
  const emailCheck = useRef({ email: null, promise: null });

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (field === 'email' && emailError) setEmailError('');
    if (error) setError('');
  };

  // Resolves true if the email is already registered. A failing live check
  // doesn't block the form; the backend re-validates on submit anyway.
  const checkDuplicate = (email) => {
    if (emailCheck.current.email === email) return emailCheck.current.promise;
    setCheckingEmail(true);
    const promise = accessRequestAPI.checkEmail(email)
      .then(({ data }) => !!data.exists)
      .catch(() => false)
      .finally(() => setCheckingEmail(false));
    emailCheck.current = { email, promise };
    return promise;
  };

  const handleEmailBlur = async () => {
    const email = form.email.trim();
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      setEmailError(INVALID_EMAIL);
      return;
    }
    if (await checkDuplicate(email)) setEmailError(ALREADY_REGISTERED);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = form.email.trim();
    if (!form.fullName.trim() || !form.phoneNumber.trim() || !email || !form.designation) {
      setError('Please fill in all fields.');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setEmailError(INVALID_EMAIL);
      setError('');
      return;
    }
    setError('');
    setSubmitting(true);
    if (await checkDuplicate(email)) {
      setEmailError(ALREADY_REGISTERED);
      setSubmitting(false);
      return;
    }
    try {
      await accessRequestAPI.submit({ ...form, source: 'landing_popup' });
      setSubmitted(true);
    } catch (err) {
      if (err.status === 409) {
        setEmailError(err.message);
      }
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="access-popup-overlay" onClick={onClose}>
      <div className="access-popup-card" role="dialog" aria-modal="true" aria-label="Request access" onClick={(e) => e.stopPropagation()}>
        <button className="access-popup-close" onClick={onClose} aria-label="Close">×</button>
        <div className="access-popup-scroll">

        {submitted ? (
          <div className="access-popup-success">
            <div className="access-success-icon">✓</div>
            <h2>Access request submitted!</h2>
            <p>The eBuddhi team will touch base with you shortly.</p>
            <button className="btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <form className="access-popup-grid" onSubmit={handleSubmit} noValidate>
            <div className="access-popup-left">
              <div className="access-popup-brand">
                <img src={logo} alt="EBuddhi logo" className="access-popup-logo" />
                <span className="access-popup-brand-tag">Learn with experts</span>
              </div>

              <h2 className="access-popup-title">
                Want to explore what our <span>experts</span> have in store for you?
              </h2>
              <p className="access-popup-subtitle">
                Take a few minutes to share your details and unlock the best deals, discounts,
                subscription offers, newsletters, and live master sessions with industry experts —
                straight from the eBuddhi platform.
              </p>

              <ul className="access-popup-benefits">
                <li>Exclusive deals &amp; discounts</li>
                <li>Early subscription offers</li>
                <li>Curated newsletters</li>
                <li>Live master sessions</li>
              </ul>

              <label>Full Name</label>
              <input
                type="text"
                placeholder="e.g. Aditi Sharma"
                value={form.fullName}
                onChange={handleChange('fullName')}
              />

              <label>Phone Number</label>
              <input
                type="tel"
                placeholder="e.g. +91 98765 43210"
                value={form.phoneNumber}
                onChange={handleChange('phoneNumber')}
              />

              <label>Email Address</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange('email')}
                onBlur={handleEmailBlur}
                className={emailError ? 'access-popup-input-error' : ''}
                aria-invalid={!!emailError}
              />
              {checkingEmail && <div className="access-popup-field-hint">Checking…</div>}
              {emailError && <div className="access-popup-field-error">{emailError}</div>}
            </div>

            <div className="access-popup-right">
              <div className="access-popup-qr-box">
                <QRCodeSVG value={ACCESS_QR_URL} size={128} fgColor="#0f172a" bgColor="#ffffff" level="H" includeMargin />
                <h3>Scan to open on your phone</h3>
                <p>Point your camera at the code to fill this form directly from your mobile device.</p>
              </div>

              <div className="access-popup-right-form">
                <label>Designation</label>
                <select value={form.designation} onChange={handleChange('designation')}>
                  <option value="">Select one</option>
                  {DESIGNATION_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>

                {error && <div className="access-popup-error">{error}</div>}

                <button type="submit" className="btn-primary access-popup-submit" disabled={submitting || !!emailError}>
                  {submitting ? 'Submitting…' : 'Get my access'}
                </button>
                <p className="access-popup-privacy">
                  🔒 Your details are stored securely and used only to personalise your eBuddhi experience.
                </p>
              </div>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
};

export default AccessRequestPopup;
